import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { FaPlus, FaXmark } from "react-icons/fa6";
import DynamicSelect from "../DynamicSelect";
import debounce from "lodash/debounce";
import { format, isValid } from "date-fns";
import { toast } from "react-toastify";
import { PathologyPrintSlip } from "./PathologyPrintSlip";
import { useAuth } from "../../utils/AuthContext";
import PathologyAgentModal from "./PathologyConfig/PathologyAgent/PathologyAgentModal";
import { saveToCache, readFromCache } from "../../services/pathologyCache";
import { queueOfflineBill, findUnsyncedBillForPatient } from "../../services/pathologyOutbox";
import { preloadAccountSummaryBulk } from "../../services/pathologyPreload";
import CreatableSelect from "react-select/creatable";


const API_URL = "https://api.muktihospital.com/api/pathology";
const DOCTOR_API_URL = "https://api.muktihospital.com/api/doctor";
const PATIENT_API_URL = "https://api.muktihospital.com/api/patient";
const ADD_PATIENT_API_URL = "https://api.muktihospital.com/api/patient/add";
const BILL_API_URL = "https://api.muktihospital.com/api/pathology-bill";
const AUTHORITIES_API_URL = "https://api.muktihospital.com/api/authorities";
const AGENT_API_URL = "https://api.muktihospital.com/api/pathology-agents";
const CORE_BASE_URL = "https://api.muktihospital.com";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

// Axios instance with timeout and retry logic
const axiosInstance = axios.create({
  timeout: 30000,
});

// Retry logic for failed requests
const retryRequest = async (config, maxRetries = 3, delay = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await axiosInstance(config);
    } catch (err) {
      if (i === maxRetries - 1) throw err;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
};

// Helper function to safely parse tests data
const parseTestsData = (tests) => {
  if (Array.isArray(tests)) {
    return tests;
  }
  if (typeof tests === 'string') {
    try {
      const parsed = JSON.parse(tests);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Error parsing tests JSON:', error);
      return [];
    }
  }
  return [];
};

const AddBill = ({
  isOpen,
  onClose,
  billDetails,
  isEditMode = false,
  onSave,
}) => {
  const [testsList, setTestsList] = useState([]);
  const [referDoctors, setReferDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authorizedByOptions, setAuthorizedByOptions] = useState([]);
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const { user } = useAuth();
  const [isCorrectionMode, setIsCorrectionMode] = useState(false);
  const [correctionReason, setCorrectionReason] = useState("");
  const [freeBillReason, setFreeBillReason] = useState("");
  const [isAdmittedZeroBill, setIsAdmittedZeroBill] = useState(false);

  const userRole = (user?.role || "").toLowerCase();
  const canMakeZeroBill = ["pathologist", "admin", "superadmin", "super admin"].includes(userRole);
  const [testRows, setTestRows] = useState([
    {
      testId: "",
      testName: "",
      reportDate: "",
      taxPercent: "",
      amount: "",
      commission: 0
    },
  ]);

  const [formData, setFormData] = useState({
      id: null,
      billNumber: "MH_PB_00001",
      referDoctorName: "",
      refer: "",
      referAmount: "",
      patientName: "",
      patientId: null,
      doctorName: "",
      donorName: "",
      donorPhone: "",
      bloodGroup: "",
      age: "",
      gender: "",
      note: "",
      discount: "",
      discountType: "percentage",
      discountSource: "hospital",
      authorizedBy:"",
      paymentMode: "cash",
      paymentAmount: "",
      netAmount: "0",
      total: "0",
      taxAmount: "0",
      dueAmount: "0",
      chequeNo: "",
      chequeDate: "",
      document: null,
      documentUrl: "",
      status: "Pending",
      userName: user?.name || user?.username || "Unknown User",
      userPhone: user?.mobile || user?.phone || "Unknown Phone",
      phoneNumber: null,
  });
  const [isNewPatientModalOpen, setIsNewPatientModalOpen] = useState(false);
  const [newPatient, setNewPatient] = useState({ name: "", phoneNumber: "" });
  const [duplicateModal, setDuplicateModal] = useState({ open: false, message: "" });
  const duplicateResolverRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [searchBillNumber, setSearchBillNumber] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [billSuggestions, setBillSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [currentDateTime, setCurrentDateTime] = useState("");
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [processingAction, setProcessingAction] = useState(null);
  const [printData, setPrintData] = useState(null);

  const paymentModes = [
    { value: "cash", label: "Cash" },
    { value: "cheque", label: "Cheque" },
    { value: "card", label: "Card" },
    { value: "upi", label: "UPI" },
  ];

  const discountTypes = [
    { value: "percentage", label: "(%)" },
    { value: "amount", label: "(৳)" },
  ];

  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Others", label: "Others" },
  ];

  const bloodGroups = [
    { value: "A+", label: "A+" },
    { value: "A-", label: "A-" },
    { value: "B+", label: "B+" },
    { value: "B-", label: "B-" },
    { value: "AB+", label: "AB+" },
    { value: "AB-", label: "AB-" },
    { value: "O+", label: "O+" },
    { value: "O-", label: "O-" },
  ];

  const discountSources = [
    { value: "hospital", label: "Hospital" },
    { value: "agent", label: "Agent" },
    { value: "doctor", label: "Doctor (Consultant)" },
  ];

  const statusOptions = [
    { value: "Pending", label: "Pending" },
    { value: "Complete", label: "Complete" },
  ];

  // Generate next bill number
  const generateNextBillNumber = (lastBillNumber) => {
    if (!lastBillNumber || !lastBillNumber.startsWith("MH_PB_")) {
      return "MH_PB_00001";
    }
    const numberPart = parseInt(lastBillNumber.replace("MH_PB_", ""), 10);
    const nextNumber = numberPart + 1;
    return `MH_PB_${nextNumber.toString().padStart(5, "0")}`;
  };

  // Fetch next bill number
  async function fetchNextBillNumber() {
    try {
      const response = await retryRequest({
        method: "get",
        url: BILL_API_URL,
        headers: { "x-api-key": API_KEY },
      });
      const bills = response.data.bills || [];
      if (bills.length === 0) return "MH_PB_00001";
      const lastBill = bills.sort((a, b) => {
        const numA = parseInt(a.billNumber.replace("MH_PB_", ""), 10);
        const numB = parseInt(b.billNumber.replace("MH_PB_", ""), 10);
        return numB - numA;
      })[0];
      return generateNextBillNumber(lastBill.billNumber);
    } catch (error) {
      console.error("Error fetching bills:", error);
      return "MH_PB_00001";
    }
  }

  // Reset form
  const resetForm = async () => {
    try {
      const nextBillNumber = await fetchNextBillNumber();
      setFormData({
        id: null,
        billNumber: nextBillNumber,
        referDoctorName: "",
        refer: "",
        agentId: null,
        referAmount: "",
        patientName: "",
        patientId: null,
        patientPhone: null,
        doctorName: "",
        donorName: "",
        age: "",
        authorizedBy: "",
        note: "",
        discount: "",
        discountType: "percentage",
        discountSource: "hospital",
        paymentMode: "cash",
        paymentAmount: "",
        netAmount: "0",
        total: "0",
        taxAmount: "0",
        dueAmount: "0",
        chequeNo: "",
        chequeDate: "",
        document: null,
        documentUrl: "",
        status: "Pending",
        userName: user?.name || user?.username || "Unknown User",
        userPhone: user?.mobile || user?.phone || "Unknown Phone",
      });
      setTestRows([
        {
          testId: "",
          testName: "",
          taxPercent: "",
          amount: "",
        },
      ]);
      setErrors({});
      setSearchBillNumber("");
      setBillSuggestions([]);
      setShowSuggestions(false);
      setSearchError("");
      setShowPrintPreview(false);
      setPrintData(null);
      setFreeBillReason("");
    } catch (err) {
      toast.error("Failed to fetch next bill number");
      setFormData((prev) => ({ ...prev, billNumber: "MH_PB_00001" }));
    }
  };

  // Load bill details in edit mode and fetch test details
  useEffect(() => {
  if (isOpen) {
    if (isEditMode && billDetails) {
      const paymentAmount = parseFloat(billDetails.paymentAmount) || 0;
      const netAmount = Math.round(parseFloat(billDetails.netAmount) || 0);
      const dueAmount = Math.round(netAmount - paymentAmount);
      const status = parseFloat(dueAmount) <= 0 ? "Complete" : "Pending";
      
      setFormData({
        id: billDetails.id || null,
        billNumber: billDetails.billNumber || "MH_PB_00001",
        createdAt: billDetails?.createdAt ? format(new Date(billDetails.createdAt), "yyyy-MM-dd HH:mm:ss") : format(new Date(), "yyyy-MM-dd HH:mm:ss"),
        referDoctorName: billDetails.referDoctorName || "",
        refer: billDetails.refer || "",
        agentId: billDetails.agentId || null,
        agentPhone: billDetails.pathologyAgent?.contactNumber || "",
        agentDisplay: billDetails.refer ? { label: billDetails.refer, value: billDetails.agentId } : null,
        referAmount: billDetails.referAmount || "",
        patientName: billDetails.patientName || "",
        patientId: billDetails.patientId || patients.find(p => p.label === billDetails.patientName)?.value || null,
        doctorName: billDetails.doctorName || billDetails.referDoctorName || "",
        donorName: billDetails.donorName || "",
        donorPhone: billDetails.donorPhone || "",
        bloodGroup: billDetails.bloodGroup || "",
        age: billDetails.age || "",
        gender: billDetails.gender || "",
        note: billDetails.note || "",
        authorizedBy: billDetails.authorizedBy || "", 
        userName: billDetails.userName || "Unknown User",
        userPhone: billDetails.userPhone || "Unknown Phone",
        discount: billDetails.discount?.toString() || "",
        discountType: billDetails.discountType === "taka" ? "amount" : (billDetails.discountType || "percentage"),
        discountSource: billDetails.discountSource || "hospital",
        paymentMode: billDetails.paymentMode || "cash",
        paymentAmount: paymentAmount.toFixed(2),
        netAmount: netAmount.toFixed(2),
        total: billDetails.total?.toString() || "0",
        taxAmount: billDetails.taxAmount?.toString() || "0",
        dueAmount,
        chequeNo: billDetails.chequeNo || "",
        chequeDate:
          billDetails.chequeDate && isValid(new Date(billDetails.chequeDate))
            ? format(new Date(billDetails.chequeDate), "yyyy-MM-dd")
            : "",
        document: null,
        documentUrl: billDetails.documentUrl || "",
        status,
      });

      setIsAdmittedZeroBill(billDetails.isAdmittedBill === true);
      setIsAdmittedZeroBill(billDetails.isAdmittedBill === true);
      if (billDetails.freeBillReason) {
        setFreeBillReason(billDetails.freeBillReason);
      } else if (billDetails.note && billDetails.note.includes("[100% Free Approved:")) {
        const match = billDetails.note.match(/\[100% Free Approved:\s*([^\]]+)\]/);
        setFreeBillReason(match && match[1] ? match[1].trim() : "");
      } else {
        setFreeBillReason("");
      }

      // Safe parsing of tests data
      const safeTests = parseTestsData(billDetails.tests);
      const updatedTestRows = safeTests.map((test) => ({
        testId: test.testId || test.testName || "",
        testName: test.testName || test.testId || "",
        reportDays: test.reportDays?.toString() || "",
        reportDate:
          test.reportDate && isValid(new Date(test.reportDate))
            ? format(new Date(test.reportDate), "yyyy-MM-dd")
            : "",
        taxPercent: test.taxPercent?.toString() || "",
        amount: test.amount?.toString() || "",
        commission: test.commission || 0
      }));
      
      setTestRows(
        updatedTestRows.length > 0
          ? updatedTestRows
          : [
              {
                testId: "",
                testName: "",
                reportDays: "",
                reportDate: format(new Date(), "yyyy-MM-dd"),
                taxPercent: "",
                amount: "",
                commission: 0
              },
            ]
      );
      updatedTestRows.forEach((row, index) => {
        if (row.testId && (!row.commission || parseFloat(row.commission) === 0)) {
          fetchTestDetails(row.testId, row.testName, index);
        }
      });
    } else {
      fetchNextBillNumber()
        .then((nextBillNumber) => {
          
          setFormData({
            id: null,
            billNumber: nextBillNumber,
            createdAt: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
            referDoctorName: "",
            patientName: "",
            patientId: null,
            doctorName: "",
            donorName: "",
            age: "",
            note: "",
            userName: user?.name || user?.username || "Unknown User",
            userPhone: user?.mobile || user?.phone || "Unknown Phone",
            discount: "",
            discountType: "percentage",
discountSource: "hospital",
            paymentMode: "cash",
            paymentAmount: "",
            netAmount: "0",
            total: "0",
            taxAmount: "0",
            dueAmount: "0",
            chequeNo: "",
            chequeDate: "",
            document: null,
            documentUrl: "",
            status: "Pending",
          });
          setTestRows([
            {
              testId: "",
              testName: "",
              reportDays: "",
              reportDate: format(new Date(), "yyyy-MM-dd"),
              taxPercent: "",
              amount: "",
              commission: 0
            },
          ]);
          setErrors({});
          setSearchBillNumber("");
          setBillSuggestions([]);
          setShowSuggestions(false);
          setSearchError("");
        })
        .catch((err) => {
          toast.error("Failed to fetch next bill number");
          setFormData({
            id: null,
            billNumber: "MH_PB_00001",
            createdAt: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
            referDoctorName: "",
            patientName: "",
            patientId: null,
            doctorName: "",
            donorName: "",
            age: "",
            note: "",
            discount: "",
            discountType: "percentage",
discountSource: "hospital",
            paymentMode: "cash",
            paymentAmount: "",
            netAmount: "0",
            total: "0",
            taxAmount: "0",
            dueAmount: "0",
            chequeNo: "",
            chequeDate: "",
            document: null,
            documentUrl: "",
            status: "Pending",
            userName: user?.name || user?.username || "Unknown User",
            userPhone: user?.mobile || user?.phone || "Unknown Phone",
          });
          setTestRows([
            {
              testId: "",
              testName: "",
              reportDays: "",
              reportDate: format(new Date(), "yyyy-MM-dd"),
              taxPercent: "",
              amount: "",
            },
          ]);
          setErrors({});
          setSearchBillNumber("");
          setBillSuggestions([]);
          setShowSuggestions(false);
          setSearchError("");
        });
    }
  }
}, [isOpen, isEditMode, billDetails, patients]);

  // Fetch tests
  useEffect(() => {
    const fetchTests = async () => {
      try {
        const res = await retryRequest({
          method: "get",
          url: API_URL,
          headers: { "x-api-key": API_KEY },
        });
        const testList = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data.data)
          ? res.data.data
          : [];
        const testOptions = testList.map((test, index) => ({
          value: test.id.toString(),
          label: test.testName || "N/A",
          testName: test.testName || "N/A",
        }));
        setTestsList(testOptions);
        saveToCache("addbill_tests", testOptions);
        saveToCache("addbill_tests_raw", testList);
      } catch (err) {
        console.error("Error fetching tests:", err);
        const cached = await readFromCache("addbill_tests");
        if (cached && cached.length > 0) {
          setTestsList(cached);
        } else {
          toast.error("Failed to load tests.");
        }
      }
    };
    if (isOpen) fetchTests();
  }, [isOpen]);

// Fetch doctors
useEffect(() => {
  const fetchDoctors = async () => {
    try {
      const res = await retryRequest({
        method: "get",
        url: DOCTOR_API_URL,
        headers: { "x-api-key": API_KEY },
      });
      const doctorList = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.data)
        ? res.data.data
        : [];
      const doctorOptions = doctorList.map((doctor) => {
        let doctorName =
          doctor.translations?.en?.name ||
          doctor.name ||
          doctor.slug ||
          "N/A";
          
        if (doctor.slug && !doctor.name && !doctor.translations?.en?.name) {
          doctorName = doctor.slug
            .replace(/-/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase());
        }

        return { 
          value: doctorName, 
          label: doctorName,
          doctorId: doctor.id 
        };
      });
      setReferDoctors(doctorOptions);
      saveToCache("addbill_doctors", doctorOptions);
    } catch (err) {
      console.error("Error fetching doctors:", err);
      const cached = await readFromCache("addbill_doctors");
      if (cached && cached.length > 0) {
        setReferDoctors(cached);
      } else {
        toast.error("Failed to load doctors.");
      }
    }
  };
  if (isOpen) fetchDoctors();
}, [isOpen]);

  // Fetch patients
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const res = await retryRequest({
          method: "get",
          url: PATIENT_API_URL,
          headers: { "x-api-key": API_KEY },
        });
        const patientList = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data.data)
          ? res.data.data
          : [];
        const patientOptions = patientList.map((patient) => ({
          value: patient.id,
          label: `${patient.name || "N/A"} (${patient.phoneNumber || "No Phone"})`,
          phoneNumber: patient.phoneNumber || null,
          patientId: patient.id || null,
          nameOnly: patient.name || "N/A" 
        }));
        console.log(patientOptions)
        setPatients(patientOptions);
        saveToCache("addbill_patients", patientOptions);
      } catch (err) {
        console.error("Error fetching patients:", err);
        const cached = await readFromCache("addbill_patients");
        if (cached && cached.length > 0) {
          setPatients(cached);
        } else {
          toast.error("Failed to load patients.");
        }
      }
    };
    if (isOpen) fetchPatients();
  }, [isOpen]);
  useEffect(() => {
      const fetchAuthorities = async () => {
          try {
              const res = await retryRequest({
                  method: "get",
                  url: AUTHORITIES_API_URL,
                  headers: { "x-api-key": API_KEY },
              });
              const authorityList = Array.isArray(res.data) ? res.data : [];
              const options = authorityList.map((auth) => ({
                  value: auth.name,
                  label: auth.name,
              }));
              setAuthorizedByOptions(options);
              saveToCache("addbill_authorities", options);
          } catch (err) {
              console.error("Error fetching authorities:", err);
              const cached = await readFromCache("addbill_authorities");
              if (cached && cached.length > 0) {
                  setAuthorizedByOptions(cached);
              } else {
                  toast.error("Failed to load authorizers.");
              }
          }
      };

      if (isOpen) {
          fetchAuthorities();
      }
    }, [isOpen]);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const res = await retryRequest({
          method: "get",
          url: AGENT_API_URL,
          headers: { "x-api-key": API_KEY },
        });
        
        const agentList = Array.isArray(res.data.agents) ? res.data.agents : [];
        
        const agentOptions = agentList.map((agent) => ({
          value: agent.id,
          label: `${agent.agentName} (${agent.contactNumber || 'N/A'})`,
          agentName: agent.agentName
        }));
        
        setAgents(agentOptions);
        saveToCache("addbill_agents", agentOptions);
      } catch (err) {
        console.error("Error fetching agents:", err);
        const cached = await readFromCache("addbill_agents");
        if (cached && cached.length > 0) {
          setAgents(cached);
        } else {
          toast.error("Failed to load pathology agents.");
        }
      }
    };

    if (isOpen) fetchAgents();
  }, [isOpen]);

  const fetchLatestBillByPatient = async (patientId) => {
    try {
      const response = await retryRequest({
        method: "get",
        url: `${BILL_API_URL}?patientId=${patientId}&limit=1`,
        headers: { "x-api-key": API_KEY },
      });

      const latestBill = response.data.bills?.[0];
      if (
        latestBill &&
        latestBill.age !== null &&
        latestBill.age !== undefined &&
        latestBill.age !== ""
      ) {
        setFormData((prev) => ({ ...prev, age: latestBill.age.toString() }));
      }
    } catch (err) {
      console.error("Error fetching patient age:", err);
    }
  };

  // Fetch bill suggestions
  const fetchBillSuggestions = useCallback(
    debounce(async (query) => {
      if (!query.trim()) {
        setBillSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      try {
        const response = await retryRequest({
          method: "get",
          url: BILL_API_URL,
          headers: { "x-api-key": API_KEY },
        });
        const bills = response.data.bills || [];
        const filteredBills = bills
          .filter((bill) =>
            bill.billNumber.toLowerCase().includes(query.toLowerCase())
          )
          .map((bill) => ({ id: bill.id, billNumber: bill.billNumber }))
          .slice(0, 5);
        setBillSuggestions(filteredBills);
        setShowSuggestions(filteredBills.length > 0);
      } catch (err) {
        console.error("Error fetching bill suggestions:", err);
        setBillSuggestions([]);
        setShowSuggestions(false);
        setSearchError("Failed to fetch suggestions.");
      }
    }, 300),
    []
  );

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchBillNumber(value);
    setSearchError("");
    fetchBillSuggestions(value);
  };

  const handleSuggestionClick = (bill) => {
    setSearchBillNumber(bill.billNumber);
    setBillSuggestions([]);
    setShowSuggestions(false);
    handleSearchBill(bill.id);
  };

  const handleSearchBill = async (billId) => {
    if (!billId) {
      setSearchError("Please select a bill.");
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    setSearchError("");
    try {
      const response = await retryRequest({
        method: "get",
        url: `${BILL_API_URL}/${billId}`,
        headers: { "x-api-key": API_KEY },
      });
      const bill = response.data.bill;
      if (!bill) {
        setSearchError("No bill found with this number.");
        setIsSearching(false);
        return;
      }
      const paymentAmount = parseFloat(bill.paymentAmount) || 0;
      const netAmount = parseFloat(bill.netAmount) || 0;
      const dueAmount = netAmount - paymentAmount;
      const status = dueAmount <= 0 ? "Complete" : "Pending";

      setFormData({
        id: bill.id,
        billNumber: bill.billNumber,
        referDoctorName: bill.referDoctorName || "",
        refer: formData.refer || "",
        agentId: bill.agentId || null,
        agentPhone: bill.pathologyAgent?.contactNumber || bill.agentPhone || "",
        referAmount: formData.referAmount ? parseInt(formData.referAmount) : 0,
        patientName: bill.patientName || "",
        patientId: bill.patientId || null,
        doctorName: bill.doctorName || bill.referDoctorName || "",
        donorName: bill.donorName || "",
        donorPhone: bill.donorPhone || "",
        bloodGroup: bill.bloodGroup || "",
        age: bill.age?.toString() || "",
        note: bill.note || "",
        authorizedBy: bill.authorizedBy || "",
        userName: bill.userName || "Unknown User",
        userPhone: bill.userPhone || "Unknown Phone",
        discount: bill.discount?.toString() || "",
        discountType: bill.discountType || "percentage",
        discountSource: bill.discountSource || "hospital",
        paymentMode: bill.paymentMode || "cash",
        paymentAmount: bill.paymentAmount?.toString() || "",
        netAmount: bill.netAmount?.toString() || "0",
        total: bill.total?.toString() || "0",
        taxAmount: bill.taxAmount?.toString() || "0",
        chequeNo: bill.chequeNo || "",
        chequeDate:
          bill.chequeDate && isValid(new Date(bill.chequeDate))
            ? format(new Date(bill.chequeDate), "yyyy-MM-dd")
            : "",
        document: null,
        documentUrl: bill.documentUrl || "",
        status,
      });

      if (bill.freeBillReason) {
        setFreeBillReason(bill.freeBillReason);
      } else if (bill.note && bill.note.includes("[100% Free Approved:")) {
        const match = bill.note.match(/\[100% Free Approved:\s*([^\]]+)\]/);
        setFreeBillReason(match && match[1] ? match[1].trim() : "");
      } else {
        setFreeBillReason("");
      }

      // Safe parsing of tests data in search results
      const safeTests = parseTestsData(bill.tests);
      const updatedTestRows = safeTests.map((test) => ({
        testId: test.testId || test.testName || "",
        testName: test.testName || test.testId || "",
        reportDays: test.reportDays?.toString() || "",
        reportDate:
          test.reportDate && isValid(new Date(test.reportDate))
            ? format(new Date(test.reportDate), "yyyy-MM-dd")
            : "",
        taxPercent: test.taxPercent?.toString() || "",
        amount: test.amount?.toString() || "",
      }));

      setTestRows(
        updatedTestRows.length > 0
          ? updatedTestRows
          : [
              {
                testId: "",
                testName: "",
                reportDays: "",
                reportDate: format(new Date(), "yyyy-MM-dd"),
                taxPercent: "",
                amount: "",
              },
            ]
      );

      // Fetch test details for each test row
      updatedTestRows.forEach((row, index) => {
        if (row.testId || row.testName) {
          fetchTestDetails(row.testId, row.testName, index);
        }
      });

      setSearchError("");
    } catch (err) {
      console.error("Error searching bill:", err);
      setSearchError(
        err.response?.status === 400
          ? "Invalid bill ID format."
          : err.response?.status === 404
          ? "Bill not found."
          : `Failed to fetch bill: ${err.response?.data?.error || err.message}`
      );
    } finally {
      setIsSearching(false);
    }
  };

  // Fetch test details
  const fetchTestDetails = async (testId, testName, rowIndex) => {
    if (!testId && !testName) return;
    try {
      const res = await retryRequest({
        method: "get",
        url: `${API_URL}/${testId || encodeURIComponent(testName)}`,
        headers: { "x-api-key": API_KEY },
      });
      const testDetails = Array.isArray(res.data) ? res.data[0] : res.data;
      const currentDate = new Date();
      setTestRows((prevRows) => {
        const updatedRows = [...prevRows];
        updatedRows[rowIndex] = {
          ...updatedRows[rowIndex],
          testId: testDetails.id?.toString() || testId || testName,
          testName: testDetails.testName,
          taxPercent: testDetails.tax?.toString() ?? "0",
          amount: (
            testDetails.standardCharge ||
            testDetails.amount ||
            ""
          )?.toString(),
          commission: testDetails.commission || 0
        };
        return updatedRows;
      });
    } catch (err) {
      console.error("Error fetching test details:", err);
      const rawTests = await readFromCache("addbill_tests_raw");
      const match =
        Array.isArray(rawTests) &&
        rawTests.find(
          (t) =>
            t.id?.toString() === testId?.toString() ||
            t.testName === testName
        );
      if (match) {
        setTestRows((prevRows) => {
          const updatedRows = [...prevRows];
          updatedRows[rowIndex] = {
            ...updatedRows[rowIndex],
            testId: match.id?.toString() || testId || testName,
            testName: match.testName,
            taxPercent: match.tax?.toString() ?? "0",
            amount: (match.standardCharge || match.amount || "")?.toString(),
            commission: match.commission || 0,
          };
          return updatedRows;
        });
        toast.info(`Offline: loaded saved details for ${match.testName}.`);
      } else {
        toast.error(`Failed to load test details for ${testName || testId}.`);
      }
    }
  };

  useEffect(() => {
  const totalAmount = testRows.reduce(
    (sum, row) => sum + (parseFloat(row.amount) || 0),
    0
  );
  const totalTaxAmount = testRows.reduce(
    (sum, row) =>
      sum +
      ((parseFloat(row.amount) || 0) * (parseFloat(row.taxPercent) || 0)) / 100,
    0
  );
  const total = Math.round(totalAmount);
  const discount = parseFloat(formData.discount) || 0;

  const commissionBase = testRows.reduce((sum, row) => {
    const c = parseFloat(row.commission);
    return sum + (isNaN(c) ? 0 : c);
  }, 0);
  const discountMoney = formData.discountType === "percentage"
    ? Math.round(((formData.discountSource === "agent" ? commissionBase : total) * discount) / 100)
    : Math.round(discount);

  let netAmount = Math.round(total + totalTaxAmount - discountMoney);
  netAmount = netAmount < 0 ? 0 : netAmount;

  const paymentAmount = parseFloat(formData.paymentAmount) || 0;
  const dueAmount = Math.round(netAmount - paymentAmount);
  
  const calculatedStatus = dueAmount <= 0 ? "Complete" : "Pending";

  setFormData((prev) => ({
    ...prev,
    total: total.toFixed(2),
    taxAmount: totalTaxAmount.toFixed(2),
    netAmount: netAmount.toFixed(2),
    dueAmount,
    status: calculatedStatus,
  }));
}, [
  testRows,
  formData.discount,
  formData.discountType,
  formData.discountSource,
  formData.paymentAmount,
  isEditMode
]);

useEffect(() => {
  if (formData.refer || formData.agentId) { 
    const totalOriginalCommission = testRows.reduce((sum, row) => {
      const comm = parseFloat(row.commission);
      return sum + (isNaN(comm) ? 0 : comm);
    }, 0);

    const discountInput = parseFloat(formData.discount) || 0;
    const billTotal = parseFloat(formData.total) || 0;

    // referAmount = GROSS commission. The agent discount is deducted ONCE on the
    // backend (netReferAmount = referAmount − discount). Subtracting it here too
    // made the commission drop twice.
    const grossCommission = totalOriginalCommission < 0 ? 0 : Math.round(totalOriginalCommission);

    // Net the agent keeps — shown for info only. Commission drops ONLY when the
    // AGENT gives the discount (same rule as the backend).
    const discountTaka =
      formData.discountType === "percentage"
        ? Math.round(((formData.discountSource === "agent" ? grossCommission : billTotal) * discountInput) / 100)
        : Math.round(discountInput);
    let netCommissionDisplay = grossCommission;
    if (discountInput > 0 && formData.discountSource === "agent") {
      netCommissionDisplay = Math.max(0, grossCommission - discountTaka);
    }

    if (
      parseFloat(formData.referAmount) !== grossCommission ||
      formData.netCommissionDisplay !== netCommissionDisplay
    ) {
      setFormData(prev => ({
        ...prev,
        totalOriginalCommission: totalOriginalCommission,
        referAmount: grossCommission,
        netCommissionDisplay: netCommissionDisplay
      }));
    }
  } else {
    if (formData.referAmount !== 0 && formData.referAmount !== "") {
      setFormData(prev => ({ ...prev, referAmount: 0, totalOriginalCommission: 0 }));
    }
  }
}, [testRows, formData.refer, formData.agentId, formData.discount, formData.discountType, formData.total, formData.discountSource]);

const handleSelectChange = (name, rowIndex) => (selectedOption) => {
    if (rowIndex !== undefined) {
      if (name === "testName") {
        const value = selectedOption ? selectedOption.value : "";
        const testName = selectedOption ? selectedOption.testName : "";
        // The same test must not be billed twice
        if (value && testRows.some((r, i) => i !== rowIndex && r.testId === value)) {
          toast.error(`"${testName}" is already added to this bill.`);
          return;
        }
        setTestRows((prevRows) => {
          const updatedRows = [...prevRows];
          updatedRows[rowIndex] = {
            ...updatedRows[rowIndex],
            testId: value,
            testName,
          };
          return updatedRows;
        });
        if (value) fetchTestDetails(value, testName, rowIndex);
      }
    } else {
      const value = selectedOption ? selectedOption.value : "";

      if (name === "patientName") {
        const pId = selectedOption ? selectedOption.value : null;
        const pName = selectedOption ? (selectedOption.nameOnly || selectedOption.label) : "";

        setFormData((prev) => ({
          ...prev,
          patientId: pId,
          patientName: pName,
          patientPhone: selectedOption ? (selectedOption.phoneNumber || null) : null,
        }));

        if (pId && pId !== "OFFLINE_NEW_PATIENT" && !isEditMode) {
          fetchLatestBillByPatient(pId);
        }
      } else if (name === "status" && value === "Complete" && isEditMode) {
        setFormData((prev) => ({
          ...prev,
          status: value,
          paymentAmount: prev.netAmount.toString(),
        }));
      } else if (name === "referDoctorName") {
        setFormData((prev) => ({
          ...prev,
          referDoctorName: value,
          doctorId: selectedOption ? selectedOption.doctorId : null,
          doctorName: value,
        }));
      } 
      else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleInputChange = (e, rowIndex) => {
    const { name, value, files } = e.target;
    if (rowIndex !== undefined) {
      setTestRows((prevRows) => {
        const updatedRows = [...prevRows];
        updatedRows[rowIndex] = { ...updatedRows[rowIndex], [name]: value };
        return updatedRows;
      });
      setErrors((prev) => ({ ...prev, [`${name}_${rowIndex}`]: "" }));
    } else {
      if (name === "document" && files) {
        const file = files[0];
        if (file) {
          if (file.size > 5 * 1024 * 1024) {
            toast.error("Document size must be less than 5MB");
            return;
          }
          if (!["image/jpeg", "image/png"].includes(file.type)) {
            toast.error("Only JPEG or PNG documents are allowed");
            return;
          }
          setFormData((prev) => ({ ...prev, document: file }));
        }
      } else if (name === "paymentAmount" && isEditMode) {
        const paymentAmount = parseFloat(value) || 0;
        const netAmount = parseFloat(formData.netAmount) || 0;
        const dueAmount = Math.round(netAmount - paymentAmount);
        setFormData((prev) => ({
          ...prev,
          paymentAmount: value,
          dueAmount,
          status: parseFloat(dueAmount) <= 0 ? "Complete" : "Pending",
        }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleNewPatientInputChange = (e) => {
    const { name, value } = e.target;
    setNewPatient((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleAddNewPatient = async () => {
    const newErrors = {};
    if (!newPatient.name) newErrors.name = "Name is required";
    if (!newPatient.phoneNumber)
      newErrors.phoneNumber = "Phone number is required";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setIsLoading(true);
    try {
      const addRes = await retryRequest({
        method: "post",
        url: ADD_PATIENT_API_URL,
        headers: { "x-api-key": API_KEY },
        data: newPatient,
      });
      // The add API returns the created patient (with its real id) directly.
      const created = addRes?.data?.patient || null;
      const res = await retryRequest({
        method: "get",
        url: PATIENT_API_URL,
        headers: { "x-api-key": API_KEY },
      });
      const patientList = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.data)
        ? res.data.data
        : [];
      const patientOptions = patientList.map((patient) => ({
        value: patient.id,
        label: `${patient.name || "N/A"} (${patient.phoneNumber || "No Phone"})`,
        phoneNumber: patient.phoneNumber || null,
        patientId: patient.id || null,
        nameOnly: patient.name || "N/A",
      }));
      setPatients(patientOptions);
      // Prefer the id from the POST response; fall back to matching by name+phone.
      const createdPatient =
        created ||
        patientList.find(
          (p) =>
            p.name === newPatient.name &&
            (p.phoneNumber || "") === (newPatient.phoneNumber || "")
        );
      setFormData((prev) => ({
        ...prev,
        patientName: createdPatient?.name || newPatient.name,
        patientId: createdPatient?.id || null,
        patientPhone: createdPatient?.phoneNumber || newPatient.phoneNumber || null,
      }));
      setNewPatient({ name: "", phoneNumber: "" });
      setIsNewPatientModalOpen(false);
      toast.success("New patient added successfully!");
    } catch (err) {
      console.error("Error adding new patient:", err);
      if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        const SENTINEL = "OFFLINE_NEW_PATIENT";
        const localOption = {
          value: SENTINEL,
          label: `${newPatient.name} (${newPatient.phoneNumber || "No Phone"})`,
          phoneNumber: newPatient.phoneNumber || null,
          patientId: null,
          nameOnly: newPatient.name,
        };
        setPatients((prev) => [
          localOption,
          ...prev.filter((o) => o.value !== SENTINEL),
        ]);
        setFormData((prev) => ({
          ...prev,
          patientName: newPatient.name,
          patientId: SENTINEL,
          patientPhone: newPatient.phoneNumber || null,
        }));
        setNewPatient({ name: "", phoneNumber: "" });
        setIsNewPatientModalOpen(false);
        toast.success("Patient added offline. They will be created on the server once the bill syncs.");
        return;
      }
      toast.error(
        `Failed to add new patient: ${err.response?.data?.error || err.message}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTestRow = () => {
    setTestRows([
      ...testRows,
      {
        testId: "",
        testName: "",
        reportDays: "",
        reportDate: "",
        taxPercent: "",
        amount: "",
        commission: 0,
      },
    ]);
  };

  const handleRemoveTestRow = (rowIndex) => {
    if (testRows.length === 1) {
      setTestRows([
        {
          testId: "",
          testName: "",
          reportDays: "",
          reportDate: "",
          taxPercent: "",
          amount: "",
        },
      ]);
    } else {
      setTestRows(testRows.filter((_, index) => index !== rowIndex));
    }
  };

  const calculateDueOrReturnAmount = () => {
    const paymentAmount = parseFloat(formData.paymentAmount) || 0;
    const netAmount = parseFloat(formData.netAmount) || 0;
    const dueAmount = Math.round(netAmount - paymentAmount);
    return {
      dueAmount: dueAmount > 0 ? dueAmount.toString() : "0",
      returnAmount: paymentAmount > netAmount ? Math.round(paymentAmount - netAmount).toString() : "0",
    };
  };

  const is100PercentHospitalDiscount =
    formData.discountSource === "hospital" &&
    ((formData.discountType === "percentage" && parseFloat(formData.discount) >= 100) ||
      (formData.discountType === "amount" &&
        parseFloat(formData.discount) >= parseFloat(formData.total) &&
        parseFloat(formData.total) > 0));

  const validateForm = () => {
    const newErrors = {};
    if (!formData.patientName)
      newErrors.patientName = "Please select a patient";
    if (!formData.age || !String(formData.age).trim()) {
      newErrors.age = "Please provide patient age";
    }
    if (!formData.gender || !String(formData.gender).trim()) {
      newErrors.gender = "Please select patient gender";
    }
    if (!formData.paymentMode)
      newErrors.paymentMode = "Please select a payment mode";
    if (isCorrectionMode && (!correctionReason || correctionReason.length < 5)) {
      newErrors.correctionReason = "A valid reason for payment correction is required";
      toast.error("Please explain why you are changing the payment amount.");
    }
    if (!formData.authorizedBy)
      newErrors.authorizedBy = "Please select an authorizer";

    if (is100PercentHospitalDiscount && (!freeBillReason || freeBillReason.trim().length < 4)) {
      newErrors.freeBillReason = "Approval reason is required for 100% Free Bill";
      toast.error("Please specify who authorized the 100% free bill and why.");
    }

    const payVal = parseFloat(formData.paymentAmount);
    if (
      formData.paymentAmount === "" ||
      formData.paymentAmount === null ||
      formData.paymentAmount === undefined ||
      isNaN(payVal) ||
      payVal < 0
    ) {
      newErrors.paymentAmount = "Please provide a valid payment amount";
    }

    const netForZeroCheck = parseFloat(formData.netAmount) || 0;
    if (netForZeroCheck > 0 && payVal <= 0 && !(isAdmittedZeroBill && canMakeZeroBill)) {
      newErrors.paymentAmount = "Payment is required, or tick 'Admitted Patient (0 Balance)' to bill on credit";
    }

    if (
      formData.discount &&
      (isNaN(parseFloat(formData.discount)) ||
        parseFloat(formData.discount) < 0)
    ) {
      newErrors.discount = "Please provide a valid discount value";
    }
    if (formData.paymentMode === "cheque") {
      if (!formData.chequeNo)
        newErrors.chequeNo = "Please provide a cheque number";
      if (!formData.chequeDate || !isValid(new Date(formData.chequeDate))) {
        newErrors.chequeDate = "Please provide a valid cheque date";
      }
    }
    testRows.forEach((row, index) => {
      if (!row.testName)
        newErrors[`testName_${index}`] = `Please select a test name for row ${
          index + 1
        }`;
      if (row.taxPercent === "" || isNaN(parseFloat(row.taxPercent))) {
        newErrors[`taxPercent_${index}`] = `Please provide a valid tax percentage for row ${index + 1}`;
      }
      if (
        !row.amount ||
        isNaN(parseFloat(row.amount)) ||
        parseFloat(row.amount) <= 0
      )
        newErrors[`amount_${index}`] = `Please provide a valid amount for row ${
          index + 1
        }`;
    });
    if (isNaN(parseFloat(formData.total)) || parseFloat(formData.total) <= 0)
      newErrors.total = "Total amount is invalid";
    if (
      isNaN(parseFloat(formData.netAmount)) ||
      parseFloat(formData.netAmount) < 0
    )
      newErrors.netAmount = "Net amount is invalid";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      Object.values(newErrors).forEach((error) => toast.error(error));
    }
    return Object.keys(newErrors).length === 0;
  };

  const updateTestQuantities = async (tests) => {
    try {
      for (const test of tests) {
      }
    } catch (err) {
      console.error("Test quantity update error:", err);
      toast.error(`Failed to update test quantities: ${err.message}`);
      throw err;
    }
  };

  const numberToWordsBD = (value) => {
    let num = Math.floor(Number(value) || 0);
    if (num === 0) return "Zero";
    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const two = (n) => (n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : ""));
    const three = (n) => {
      const h = Math.floor(n / 100);
      const r = n % 100;
      return (h ? ones[h] + " Hundred" + (r ? " " : "") : "") + (r ? two(r) : "");
    };
    let words = "";
    const crore = Math.floor(num / 10000000); num %= 10000000;
    const lakh = Math.floor(num / 100000); num %= 100000;
    const thousand = Math.floor(num / 1000); num %= 1000;
    if (crore) words += three(crore) + " Crore ";
    if (lakh) words += two(lakh) + " Lakh ";
    if (thousand) words += two(thousand) + " Thousand ";
    if (num) words += three(num);
    return words.trim();
  };

  const handleSaveAndPrint = async () => {
    setIsLoading(true);
    setProcessingAction('print');
    const result = await handleSave(true);
    if (!result || result.success === false) {
        console.log("Print aborted due to validation errors.");
        setIsLoading(false);
        setProcessingAction(null);
        return; 
    }

    const { payload, tests, billNumber, newBill } = result;

    const signatureFooter = (
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px', paddingLeft: '10px', paddingRight: '10px' }}>
      
        <div style={{ textAlign: 'center', width: '220px' }}>
          <div style={{ borderTop: '1px solid #333', height: '1px', marginBottom: '8px' }}></div>
          <p style={{ margin: '0', fontWeight: 'bold', fontSize: '11pt', textTransform: 'capitalize' }}>
            {formData.userName || "N/A"}
          </p>
          <p style={{ margin: '5px 0 0 0', fontSize: '9pt', color: '#555' }}>
            Prepared By
          </p>
        </div>

        <div style={{ textAlign: 'center', width: '220px' }}>
          <div style={{ borderTop: '1px solid #333', height: '1px', marginBottom: '8px' }}></div>
          <p style={{ margin: '0', fontWeight: 'bold', fontSize: '11pt' }}>
            {formData.authorizedBy || "N/A"}
          </p>
          <p style={{ margin: '5px 0 0 0', fontSize: '9pt', color: '#555' }}>
            Authorized By
          </p>
        </div>
      </div>
    );

    const billInfo = (
      <>
        <div>
          <p><strong>Bill No:</strong> {billNumber}</p>
          <p><strong>Patient Name:</strong> {payload.patientName}</p>
          {(() => {
            const foundPatient = patients.find(p => 
              p.value === payload.patientId || 
              p.nameOnly === payload.patientName ||
              p.label === payload.patientName 
            );

            if (foundPatient?.phoneNumber) {
              return (
                <p>
                  <strong>Mobile:</strong> {foundPatient.phoneNumber}
                </p>
              );
            }
            return null;
          })()}
          {payload.patientId && (
            <p><strong>Patient ID:</strong> {payload.patientId}</p>
          )}
        </div>
        <div>
          <p><strong>Date:</strong> {payload.createdAt ? format(new Date(payload.createdAt), "dd MMM yyyy, hh:mm a") : "N/A"}</p>
          <p><strong>Age:</strong> {payload.age || "N/A"}</p>
          <p><strong>Blood Group:</strong> {payload.bloodGroup || "N/A"}</p>
          <p><strong>Donor:</strong> {payload.donorName || "N/A"}</p>
        </div>
        <p className="full"><strong>Doctor Name:</strong> {payload.doctorName || "N/A"}</p>
      </>
    );

    const table = (
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="p-2 sm:p-3 border font-semibold">Test Name</th>
            <th className="p-2 sm:p-3 border font-semibold text-right">Amount (৳)</th>
          </tr>
        </thead>
        <tbody>
          {tests.map((row, index) => (
            <tr key={index} className="border-t">
              <td className="p-2 sm:p-3 border">{row.testName || "N/A"}</td>
              <td className="p-2 sm:p-3 border text-right">{row.amount.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );

    const summary = (
      <div className="flex flex-col">
        <div className="flex justify-between">
          <span>Total:</span>
          <span>৳{payload.total.toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span>Discount ({payload.discountType === "percentage" ? "%" : "৳"}):</span>
          <span>{payload.discount.toFixed(2)}</span>
        </div>
        {payload.returnAmount > 0 && (
          <div className="flex justify-between">
            <span>Return Amount:</span>
            <span>৳{payload.returnAmount.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span>Net Amount:</span>
          <span>৳{payload.netAmount.toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span>Net Payable:</span>
          <span>৳{(payload.paymentAmount || 0).toFixed(2)}</span>
        </div>
        {payload.dueAmount > 0 && (
          <div className="flex justify-between">
            <span>Due:</span>
            <span>৳{payload.dueAmount.toFixed(2)}</span>
          </div>
        )}
      </div>
    );

    const printNoteContent = [
      payload.freeBillReason ? `[Free Bill Approval: ${payload.freeBillReason}]` : "",
      payload.note || ""
    ].filter(Boolean).join(" ");

    const note = printNoteContent ? (
      <div className="note-box">
        <strong>Note:</strong> {printNoteContent}
      </div>
    ) : null;

   const isFree = Number(payload.netAmount) === 0 && Number(payload.total) > 0;
   const amountInWords = isFree ? "Zero Taka Only (100% Hospital Free)" : numberToWordsBD(payload.netAmount) + " Taka Only";
   const isPaid = (Number(payload.dueAmount) || 0) <= 0;
   
   setPrintData({ 
     billInfo, 
     table, 
     summary, 
     note, 
     billNumber, 
     signatureFooter, 
     amountInWords, 
     isPaid,
     isFree
   });
   setShowPrintPreview(true);

    setTimeout(() => {
      const elementsToHide = document.querySelectorAll(".print-hidden");
      elementsToHide.forEach((el) => (el.style.display = "none"));

      const printLayout = document.querySelector(".print-preview");
      if (printLayout) {
        printLayout.style.display = "block";
      }

      window.print();

      setTimeout(() => {
        elementsToHide.forEach((el) => (el.style.display = ""));
        if (printLayout) {
          printLayout.style.display = "none";
        }
        setShowPrintPreview(false);
        setIsLoading(false);
        setProcessingAction(null);
        const successMessage = formData.id ? "Bill updated successfully!" : "Bill added successfully!";
        onSave(successMessage);
        const newBill = formData.id
          ? {
              ...payload,
              id: formData.id,
              createdAt: payload.createdAt,
              documentUrl: formData.documentUrl || "",
            }
          : payload;
        if (!formData.id) {
          resetForm();
        }
      }, 500);
    }, 1000);
  };

  const askDuplicateConfirm = (message) => {
    return new Promise((resolve) => {
      duplicateResolverRef.current = resolve;
      setDuplicateModal({ open: true, message });
    });
  };

  const resolveDuplicate = (proceed) => {
    setDuplicateModal({ open: false, message: "" });
    if (duplicateResolverRef.current) {
      duplicateResolverRef.current(proceed);
      duplicateResolverRef.current = null;
    }
  };

 const handleSave = async (isFromPrint = false) => {
    if (!validateForm()) return { success: false };
    const { dueAmount, returnAmount } = calculateDueOrReturnAmount();
    if (!isFromPrint) {
        setIsLoading(true);
        setProcessingAction('save');
    }

    const ageValue =
      formData.age && String(formData.age).trim() !== ""
        ? String(formData.age).trim().toUpperCase()
        : null;

    const payload = {
      billNumber: formData.billNumber,
      createdAt: formData.id 
        ? (formData.createdAt || new Date().toISOString()) 
        : new Date().toISOString(),
      referDoctorName: formData.referDoctorName || "",
      refer: formData.refer || "",
      agentId: formData.agentId || null,
      referAmount: formData.referAmount ? parseInt(formData.referAmount) : 0,
      patientName: formData.patientName,
      patientId:
        formData.patientId && formData.patientId !== "OFFLINE_NEW_PATIENT"
          ? formData.patientId
          : null,
      patientPhone: formData.patientPhone || null,
      doctorName: formData.doctorName || "",
      doctorId: formData.doctorId || null,
      donorName: formData.donorName || "",
      donorPhone: formData.donorPhone || "",
      bloodGroup: formData.bloodGroup || "",
      age: ageValue,
      gender: formData.gender || "",
      authorizedBy: formData.authorizedBy,
      note: formData.note || "",
      freeBillReason: is100PercentHospitalDiscount ? freeBillReason.trim() : null,
      discount: parseFloat(formData.discount) || 0,
      discountType: formData.discountType,
      discountSource: formData.discountSource || "hospital",
      userName: formData.userName,
      userPhone: formData.userPhone,
      paymentMode: formData.paymentMode,
      paymentAmount: parseFloat(formData.paymentAmount) || 0,
      isAdmittedZeroBill: isAdmittedZeroBill && canMakeZeroBill,
      netAmount: parseFloat(formData.netAmount) || 0,
      total: parseFloat(formData.total) || 0,
      taxAmount: parseFloat(formData.taxAmount) || 0,
      dueAmount: parseFloat(dueAmount) || 0,
      reason: correctionReason,
      returnAmount: parseFloat(returnAmount) || 0,
      chequeNo: formData.chequeNo || "",
      chequeDate: formData.chequeDate || "",
      tests: testRows.map((row) => ({
        testId: row.testId,
        testName: row.testName,
        taxPercent: row.taxPercent === "" ? 0 : parseFloat(row.taxPercent),
        amount: parseFloat(row.amount) || 0,
      })),
      status: (parseFloat(dueAmount) || 0) <= 0 ? "Complete" : formData.status,
    };

    try {
      let response;
      let newBill;
      if (formData.id) {
        if (isNaN(parseInt(formData.id))) {
          toast.error("Invalid bill ID for update.");
          return { success: false };
        }
        if (formData.document) {
          const formDataPayload = new FormData();
          Object.entries(payload).forEach(([key, value]) => {
            if (key === "tests" && Array.isArray(value)) {
              value.forEach((test, index) => {
                Object.entries(test).forEach(([testKey, testValue]) => {
                  formDataPayload.append(`tests[${index}][${testKey}]`, testValue);
                });
              });
            } else {
              formDataPayload.append(key, value);
            }
          });
          formDataPayload.append("document", formData.document);
          response = await retryRequest({
            method: "put",
            url: `${BILL_API_URL}/edit/${formData.id}`,
            headers: { "x-api-key": API_KEY },
            data: formDataPayload,
          });
        } else {
          response = await retryRequest({
            method: "put",
            url: `${BILL_API_URL}/edit/${formData.id}`,
            headers: { "x-api-key": API_KEY },
            data: payload,
          });
        }
        newBill = {
          ...payload,
          id: formData.id,
          createdAt: response.data.bill?.createdAt || billDetails?.createdAt || new Date(),
          documentUrl: response.data.bill?.documentUrl || formData.documentUrl,
        };
        if (!isFromPrint) {
          await onSave("Bill updated successfully!");
          setIsCorrectionMode(false);
          setCorrectionReason("");
          setFreeBillReason("");
        }
      } else {
        if (formData.document) {
          const formDataPayload = new FormData();
          Object.entries(payload).forEach(([key, value]) => {
            if (key === "tests" && Array.isArray(value)) {
              value.forEach((test, index) => {
                Object.entries(test).forEach(([testKey, testValue]) => {
                  formDataPayload.append(`tests[${index}][${testKey}]`, testValue);
                });
              });
            } else {
              formDataPayload.append(key, value);
            }
          });
          formDataPayload.append("document", formData.document);
          response = await retryRequest({
            method: "post",
            url: `${BILL_API_URL}/add`,
            headers: { "x-api-key": API_KEY },
            data: formDataPayload,
          });
        } else {
          response = await retryRequest({
            method: "post",
            url: `${BILL_API_URL}/add`,
            headers: { "x-api-key": API_KEY },
            data: payload,
          });
        }
        newBill = response.data.bill;
        await updateTestQuantities(payload.tests);
        preloadAccountSummaryBulk().catch(() => {});
        if (!isFromPrint) {
            await onSave("Bill added successfully!");
            setFreeBillReason("");
            await resetForm();
        }
      }
      return {
        success: true,
        payload,
        tests: payload.tests,
        billNumber: newBill?.billNumber || "N/A",
      };
    } catch (err) {
      console.error("Error saving bill:", err.response?.data || err.message);
      const isNewBill = !formData.id;
      const isNetworkError = err.code === "ERR_NETWORK";
      const isReallyOffline = isNetworkError && !navigator.onLine;

      if (isReallyOffline && isNewBill && formData.document) {
        toast.error(
          "Bills with attached documents cannot be saved offline. Please remove the file and try again."
        );
        return { success: false };
      }

      if (isReallyOffline && isNewBill && !formData.document) {
        const getCachedBills = async () => {
          const statuses = ["Active", "Pending", "Complete", "All"];
          let all = [];
          for (const s of statuses) {
            const c = await readFromCache(`bills_${s}`);
            if (Array.isArray(c)) all = all.concat(c);
          }
          return all;
        };

        const existing = await findUnsyncedBillForPatient(
          payload.patientId,
          payload.patientName,
          payload.userPhone,
          getCachedBills
        );

        if (existing) {
          const proceed = await askDuplicateConfirm(
            `A bill already exists for this patient (${payload.patientName}) with bill number (${existing.provisionalBillNumber}).\n\nAre you sure you want to create another new bill?`
          );
          if (!proceed) {
            toast.info("Creation of new bill has been canceled.");
            return { success: false };
          }
        }

        const clientId =
          (crypto && crypto.randomUUID && crypto.randomUUID()) ||
          `off-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const provisionalBillNumber = `MH_PB_OFF_${clientId.slice(0, 4)}`;

        const queued = await queueOfflineBill(
          payload,
          clientId,
          provisionalBillNumber,
          { userName: payload.userName }
        );

        if (queued) {
          toast.success(
            `Saved offline (${provisionalBillNumber}). It will sync automatically when the connection is restored.`
          );
          if (!isFromPrint) {
            setFreeBillReason("");
            await resetForm();
          }
          return {
            success: true,
            offline: true,
            payload,
            tests: payload.tests,
            billNumber: provisionalBillNumber,
          };
        }
        toast.error("Failed to save offline. Please try again.");
        return { success: false };
      }

      const errorMessage =
        err.code === "ERR_NETWORK"
          ? "Cannot reach server. Check if backend is running."
          : err.response?.data?.error
          ? `Server error: ${err.response.status} ${err.response.data.error}`
          : err.message;
      toast.error(`Error saving bill: ${errorMessage}`);
      return { success: false };
    } finally {
      if (!isFromPrint) {
        setIsLoading(false);
        setProcessingAction(null);
      }
    }
  };

  if (!isOpen) return null;
  const { dueAmount, returnAmount } = calculateDueOrReturnAmount();

  const handleAgentSaveSuccess = async (newAgent) => {
    if (newAgent?.__offline) {
      const localOption = {
        value: newAgent.id,
        label: `${newAgent.name} (${newAgent.contactNo || 'No Phone'})`,
        agentName: newAgent.name,
      };
      setAgents((prev) => [
        localOption,
        ...prev.filter((o) => o.value !== newAgent.id),
      ]);
      setFormData((prev) => ({
        ...prev,
        agentId: newAgent.id,
        refer: newAgent.name,
      }));
      toast.success("Agent added offline and selected!");
      return;
    }

    try {
      const res = await retryRequest({
        method: "get",
        url: AGENT_API_URL,
        headers: { "x-api-key": API_KEY },
      });

      const agentList = Array.isArray(res.data.agents) ? res.data.agents : [];
      
      const agentOptions = agentList.map((agent) => ({
        value: agent.id, 
        label: `${agent.agentName} (${agent.contactNumber || 'No Phone'})`, 
        agentName: agent.agentName 
      }));
      
      setAgents(agentOptions);

      setFormData(prev => ({
        ...prev,
        agentId: newAgent.id,
        refer: newAgent.name 
      }));
      
      toast.success("Agent added and selected!");
    } catch (err) {
      console.error("Failed to refresh agents:", err);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white shadow-lg w-full max-w-full max-h-[100vh] h-full overflow-y-auto print-wrapper">
        {/* Modal Header */}
        <div className="bg-[#ececec] p-5 flex flex-col md:flex-row justify-between items-center mb-4 relative z-50 border-b border-dashed border-M-text-color/50">
          <div className="flex flex-col items-center md:items-start">
            <h2 className="text-lg font-bold text-gray-700">
              BILL NO: {formData.id ? formData.billNumber : "New (auto-assigned on save)"}
            </h2>
            <span className="text-sm text-gray-500">{currentDateTime}</span>
          </div>
          <div className="flex items-start justify-center flex-wrap gap-2 mt-3 xl:mt-0 relative z-50">
            <div className="w-64">
              <DynamicSelect
                options={patients}
                value={
                  patients.find(
                    (option) => 
                      option.value === formData.patientId ||
                      option.nameOnly === formData.patientName
                  ) || 
                  (formData.patientName ? { label: formData.patientName, value: formData.patientName } : null)
                }
                onChange={handleSelectChange("patientName")}
                isSearchable
                placeholder="Search Patient"
                className={`w-full rounded-md border ${
                  errors.patientName ? "border-red-500" : "border-gray-300"
                } bg-gray-50 shadow-sm`}
                isDisabled={isSearching}
              />
              {errors.patientName && (
                <p className="text-red-500 text-xs mt-1.5">
                  {errors.patientName}
                </p>
              )}
            </div>
            <div className="relative flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchBillNumber}
                  onChange={handleSearchInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !showSuggestions) {
                      e.preventDefault();
                      handleSearchBill();
                    }
                  }}
                  onFocus={() =>
                    billSuggestions.length > 0 && setShowSuggestions(true)
                  }
                  onBlur={() =>
                    setTimeout(() => setShowSuggestions(false), 200)
                  }
                  placeholder="Search Bill Number (e.g., MH_PB_00001)"
                  className={`input-field ${
                    searchError ? "border-red-500" : "border-gray-300"
                  } bg-gray-50 rounded-md shadow-sm`}
                  disabled={isSearching}
                />
                {showSuggestions && billSuggestions.length > 0 && (
                  <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                    {billSuggestions.map((bill) => (
                      <li
                        key={bill.id}
                        onClick={() => handleSuggestionClick(bill)}
                        className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                      >
                        {bill.billNumber}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <button
              onClick={() => setIsNewPatientModalOpen(true)}
              className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 text-sm"
              disabled={isSearching}
            >
              New Patient
            </button>
            <button
              className="text-gray-500 hover:text-gray-700"
              onClick={onClose}
              disabled={isSearching}
            >
              <FaXmark size={20} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-5 overflow-y-auto md:h-[calc(100vh-200px)]">
          <div className="overflow-x-scroll lg:overflow-visible relative z-10">
            <div className="border-b border-dashed pb-4 mb-4 min-w-[800px] w-full">
              <div className="grid grid-cols-3 gap-4 mb-2">
                <div className="col-span-1">
                  <label className="label">
                    Test Name <span className="text-red-500">*</span>
                  </label>
                </div>
                <div className="col-span-1">
                  <label className="label">
                    Vat (%) <span className="text-red-500">*</span>
                  </label>
                </div>
                <div className="col-span-1">
                  <label className="label">
                    Amount (৳) <span className="text-red-500">*</span>
                  </label>
                </div>
              </div>
              {testRows.map((row, rowIndex) => (
                <div key={rowIndex} className="grid grid-cols-3 gap-4 mb-4">
                  <div className="col-span-1">
                    <DynamicSelect
                        options={testsList.filter(
                          (test) =>
                            test.value === row.testId ||
                            !testRows.some((r, i) => i !== rowIndex && r.testId === test.value)
                        )}
                        value={
                          testsList.find((test) => test.value === row.testId) || null
                        }
                        onChange={(selectedOption) =>
                          handleSelectChange("testName", rowIndex)(selectedOption)
                        }
                        placeholder="Select Test"
                        isSearchable
                        isDisabled={isSearching}
                        className={`w-full rounded-md border ${
                          errors[`testName_${rowIndex}`] ? "border-red-500" : "border-gray-300"
                        } bg-gray-50 shadow-sm`}
                      />
                    {errors[`testName_${rowIndex}`] && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors[`testName_${rowIndex}`]}
                      </p>
                    )}
                    {rowIndex === testRows.length - 1 && (
                      <button
                        onClick={handleAddTestRow}
                        className="flex items-center mt-3 gap-1 bg-M-primary-color/90 text-sm text-white py-1 px-3 rounded-sm hover:bg-M-primary-color transition-all duration-200"
                        disabled={isSearching}
                      >
                        <FaPlus size={10} /> Add
                      </button>
                    )}
                  </div>
                  <div className="col-span-1">
                    <div className="flex items-center border border-gray-300 rounded-md">
                      <input
                        type="number"
                        name="taxPercent"
                        value={row.taxPercent}
                        onChange={(e) => handleInputChange(e, rowIndex)}
                        className="flex-1 text-sm border-none rounded-l-md p-2 focus:outline-none w-full bg-gray-100"
                        placeholder="0"
                        step="0.01"
                        readOnly
                        disabled={isSearching}
                      />
                      <span className="px-3 text-sm text-gray-700">%</span>
                    </div>
                  </div>
                  <div className="col-span-1">
                    <div className="flex gap-2 items-center">
                      <input
                        type="number"
                        name="amount"
                        value={row.amount}
                        onChange={(e) => handleInputChange(e, rowIndex)}
                        className="input-field bg-gray-100 flex-1"
                        placeholder="0.00"
                        step="0.01"
                        readOnly
                        disabled={isSearching}
                      />
                      <button
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleRemoveTestRow(rowIndex)}
                        disabled={isSearching}
                      >
                        <FaXmark size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
                {/* ADD THESE NEW FIELDS BELOW */}
                <div>
                  <label className="label">Refer Agent</label>
                  <DynamicSelect
                    options={agents}
                    value={
                      agents.find((option) => option.value === formData.agentId) || 
                      (formData.refer ? { 
                          label: `${formData.refer} (${formData.agentPhone || 'N/A'})`, 
                          value: formData.agentId 
                      } : null)
                    }
                    onChange={(selectedOption) => {
                      setFormData(prev => ({
                        ...prev,
                        agentId: selectedOption ? selectedOption.value : null,
                        refer: selectedOption ? selectedOption.agentName : "",
                        agentPhone: selectedOption ? selectedOption.label.split('(')[1]?.replace(')', '') : "" 
                      }));
                      setErrors(prev => ({ ...prev, refer: "" }));
                    }}
                    isSearchable
                    placeholder="Select Refer Agent"
                    isDisabled={isSearching}
                    className={`w-full rounded-md border ${
                      errors.refer ? "border-red-500" : "border-gray-300"
                    } bg-gray-50 shadow-sm`}
                  />
                  {errors.refer && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.refer}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => setIsAgentModalOpen(true)}
                    className="text-M-primary-color text-xs font-bold hover:underline flex items-center gap-1 mt-2"
                  >
                    <FaPlus size={8} /> New Agent
                  </button>
                </div>
                <div>
                  <label className="label">Calculated Commission (৳)</label>
                  <input
                    type="number"
                    value={formData.totalOriginalCommission || 0}
                    className="input-field bg-gray-50 font-bold text-M-primary-color"
                    placeholder="0"
                    readOnly
                  />
                </div>
                <div>
                  <label className="label">Net Commission (৳)</label>
                  <input
                    type="number"
                    value={formData.netCommissionDisplay ?? formData.referAmount ?? 0}
                    className="input-field bg-gray-50 font-bold text-green-600"
                    placeholder="0"
                    readOnly
                  />
                </div>
                <div>
                <label className="label font-medium text-black">
                  Authorized By <span className="text-red-600">*</span>
                </label>
                <DynamicSelect
                    options={authorizedByOptions}
                    value={
                      authorizedByOptions.find(
                        (option) => option.value === formData.authorizedBy
                      ) || null
                    }
                    onChange={(selectedOption) =>
                      handleSelectChange("authorizedBy")(selectedOption)
                    }
                    placeholder="Select Authorizer"
                    isSearchable
                    isDisabled={isSearching}
                    className={`w-full rounded-md border ${
                      errors.authorizedBy ? "border-red-500" : "border-gray-300"
                    } bg-gray-50 shadow-sm`}
                  />
                {errors.authorizedBy && (
                  <p className="text-red-500 text-xs mt-1.5">
                    {errors.authorizedBy}
                  </p>
                )}
              </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-8 mt-8">
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="label">Consultant</label>
                  <CreatableSelect
                    options={referDoctors}
                    value={
                      referDoctors.find(
                        (option) => option.value === formData.referDoctorName
                      ) || (formData.referDoctorName ? { label: formData.referDoctorName, value: formData.referDoctorName } : null)
                    }
                    onChange={(selectedOption) => {
                      const val = selectedOption ? selectedOption.value : "";
                      setFormData((prev) => ({
                        ...prev,
                        referDoctorName: val,
                        doctorId: selectedOption?.doctorId || null,
                        doctorName: val,
                      }));
                      setErrors((prev) => ({ ...prev, referDoctorName: "" }));
                    }}
                    onCreateOption={(inputValue) => {
                      const cleanName = inputValue.trim();
                      setFormData((prev) => ({
                        ...prev,
                        referDoctorName: cleanName,
                        doctorId: null,
                        doctorName: cleanName,
                      }));
                      setErrors((prev) => ({ ...prev, referDoctorName: "" }));
                    }}
                    formatCreateLabel={(inputValue) => `Use "${inputValue}" (Outside Consultant)`}
                    isSearchable
                    isClearable
                    placeholder="Select or type consultant name..."
                    className="w-full text-sm"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "38px",
                        backgroundColor: "#f9fafb",
                        borderColor: "#d1d5db",
                        borderRadius: "0.375rem",
                        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                    isDisabled={isSearching}
                  />
                </div>
                <div>
                  <label className="label">Doctor Name</label>
                  <input
                    type="text"
                    name="doctorName"
                    value={formData.doctorName}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="Enter doctor name"
                    disabled={isSearching}
                  />
                </div>
              </div>
              {/* Payment Area */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="label">
                    Payment Mode <span className="text-red-500">*</span>
                  </label>
                  <DynamicSelect
                    options={paymentModes}
                    value={
                      paymentModes.find(
                        (option) => option.value === formData.paymentMode
                      ) || (paymentModes.length > 0 ? paymentModes[0] : null)
                    }
                    onChange={handleSelectChange("paymentMode")}
                    isSearchable
                    placeholder="Select Payment Mode"
                    className={`w-full rounded-md border ${
                      errors.paymentMode ? "border-red-500" : "border-gray-300"
                    } bg-gray-50 shadow-sm`}
                    isDisabled={isSearching}
                  />
                  {errors.paymentMode && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.paymentMode}
                    </p>
                  )}
                </div>
                <div>
                  <label className="label">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <DynamicSelect
                    options={statusOptions}
                    value={
                      statusOptions.find(
                        (option) => option.value === formData.status
                      ) || null
                    }
                    onChange={handleSelectChange("status")}
                    isSearchable={false}
                    placeholder="Select Status"
                    className={`w-full rounded-md border ${
                      errors.status ? "border-red-500" : "border-gray-300"
                    } bg-gray-50 shadow-sm`}
                    isDisabled={isSearching}
                  />
                  {errors.status && (
                    <p className="text-red-500 text-xs mt-1">{errors.status}</p>
                  )}
                </div>
              </div>

            {formData.paymentMode === "cheque" && (
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="label">
                    Cheque No <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="chequeNo"
                    value={formData.chequeNo}
                    onChange={handleInputChange}
                    className={`input-field ${
                      errors.chequeNo ? "border-red-500" : ""
                    }`}
                    placeholder="Cheque number"
                    disabled={isSearching}
                  />
                  {errors.chequeNo && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.chequeNo}
                    </p>
                  )}
                </div>
                <div>
                  <label className="label">
                    Cheque Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="chequeDate"
                    value={formData.chequeDate}
                    onChange={handleInputChange}
                    className={`input-field ${
                      errors.chequeDate ? "border-red-500" : ""
                    }`}
                    disabled={isSearching}
                  />
                  {errors.chequeDate && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.chequeDate}
                    </p>
                  )}
                </div>
                <div>
                  <label className="label">Attach Document</label>
                  {formData.documentUrl && (
                    <div className="mb-2">
                      <p className="text-sm text-gray-600">
                        Current Document:{" "}
                        <a
                          href={`${CORE_BASE_URL}${formData.documentUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          View
                        </a>
                      </p>
                    </div>
                  )}
                  <input
                    type="file"
                    name="document"
                    accept="image/jpeg,image/png"
                    onChange={handleInputChange}
                    className="mt-1 block w-full text-sm text-gray-600 border border-gray-300 rounded-md p-2"
                    disabled={isSearching}
                  />
                  {formData.document && (
                    <div className="mt-2">
                      <img
                        src={URL.createObjectURL(formData.document)}
                        alt="Uploaded Preview"
                        className="w-24 h-24 object-cover rounded-md"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="label">Age <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="age"
                  value={formData.age}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="e.g. 25, 5M, 10D, 2Y"
                  disabled={isSearching}
                />
                {errors.age && (
                  <p className="text-red-500 text-xs mt-1">{errors.age}</p>
                )}
              </div>
              <div>
                <label className="label">Gender <span className="text-red-500">*</span></label>
                <DynamicSelect
                  options={genderOptions}
                  value={genderOptions.find((o) => o.value.toLowerCase() === String(formData.gender || "").toLowerCase()) || null}
                  onChange={handleSelectChange("gender")}
                  isSearchable={false}
                  placeholder="Select Gender"
                  isDisabled={isSearching}
                  className="w-full rounded-md border border-gray-300 bg-gray-50 shadow-sm"
                />
                {errors.gender && (
                  <p className="text-red-500 text-xs mt-1">{errors.gender}</p>
                )}
              </div>
              <div>
                <label className="label">Blood Group</label>
                <DynamicSelect
                  options={bloodGroups}
                  value={bloodGroups.find((o) => o.value === formData.bloodGroup) || null}
                  onChange={handleSelectChange("bloodGroup")}
                  isSearchable
                  placeholder="Select Blood Group"
                  isDisabled={isSearching}
                  className="w-full rounded-md border border-gray-300 bg-gray-50 shadow-sm"
                />
              </div>
              <div>
                <label className="label">Donor</label>
                <input
                  type="text"
                  name="donorName"
                  value={formData.donorName}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Enter donor name"
                  disabled={isSearching}
                />
              </div>
              <div>
                <label className="label">Donor Phone</label>
                <input
                  type="text"
                  name="donorPhone"
                  value={formData.donorPhone}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="Enter donor phone"
                  disabled={isSearching}
                />
              </div>
            </div>
            {/* Note Area */}
            <div className="mb-4">
              <label className="label">Note</label>
              <textarea
                name="note"
                value={formData.note}
                onChange={handleInputChange}
                className="input-field"
                rows="3"
                placeholder="Enter any note"
                disabled={isSearching}
              />
            </div>
          </div>
          <div>
            {is100PercentHospitalDiscount && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg animate-fadeIn">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-bold text-amber-900">
                    Free Bill Approval Note / Reason <span className="text-red-600">*</span>
                  </label>
                  <span className="text-[10px] font-bold bg-green-100 text-green-800 border border-green-300 px-2 py-0.5 rounded">
                    Agent Commission Intact: ৳{formData.referAmount || 0}
                  </span>
                </div>
                <input
                  type="text"
                  placeholder="e.g. Approved by MD Sir / Staff family / Poor fund"
                  value={freeBillReason}
                  onChange={(e) => setFreeBillReason(e.target.value)}
                  className="w-full border border-amber-300 rounded p-2 text-xs outline-none bg-white focus:ring-1 focus:ring-amber-500 font-medium"
                />
              </div>
            )}
            {/* Calculation Area */}
            <table className="w-full table-fixed border-collapse ml-auto">
              <tbody>
                {/* Sub Total */}
                <tr>
                  <td className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                    Sub Total (৳)
                  </td>
                  <td className="border border-gray-200 py-2 px-3 text-end">
                    <input
                      name="total"
                      type="number"
                      className="input-field text-end"
                      value={formData.total}
                      readOnly
                      min="0"
                      step="1"
                      required
                    />
                  </td>
                </tr>
                {/* Vat Area */}
                <tr>
                  <td className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                    Vat (৳)
                  </td>
                  <td className="border border-gray-200 py-2 px-3 text-end">
                    <input
                      type="number"
                      value={formData.taxAmount}
                      readOnly
                      className="input-field bg-gray-100 text-end"
                      step="0.01"
                    />
                  </td>
                </tr>
                {/* Discount Source Area */}
                <tr>
                  <td className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                    Discount Source
                  </td>
                  <td className="border border-gray-200 py-2 px-3">
                    <DynamicSelect
                      options={discountSources}
                      value={discountSources.find((o) => o.value === formData.discountSource) || discountSources[0]}
                      onChange={handleSelectChange("discountSource")}
                      isSearchable={false}
                      isDisabled={isSearching || !!formData.id}
                    />
                  </td>
                </tr>
                {/* Discount Area */}
                <tr>
                  <td className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                    <div className="flex items-center gap-3 justify-end">
                      Discount{" "}
                      <DynamicSelect
                        options={discountTypes}
                        value={
                          discountTypes.find(
                            (option) => option.value === formData.discountType
                          ) || null
                        }
                        onChange={handleSelectChange("discountType")}
                        isSearchable={false}
                        isDisabled={isSearching || !!formData.id}
                      />
                    </div>
                  </td>
                  <td className="border border-gray-200 py-2 px-3 text-end">
                    <input
                      type="number"
                      name="discount"
                      value={formData.discount}
                      onChange={handleInputChange}
                      readOnly={!!formData.id}
                      className={`input-field flex-1 text-end ${
                        errors.discount ? "border-red-500" : ""
                      } ${formData.id ? "bg-gray-100 cursor-not-allowed" : ""}`}
                      placeholder="0"
                      step={
                        formData.discountType === "percentage"
                          ? "0.01"
                          : "0.01"
                      }
                      disabled={isSearching}
                    />
                  </td>
                </tr>
                {/* Net Total */}
                <tr>
                  <td className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                    Net Total (৳)
                  </td>
                  <td className="border border-gray-200 py-2 px-3">
                    <input
                      type="number"
                      value={formData.netAmount}
                      readOnly
                      className="input-field bg-gray-100 text-end"
                      step="1" 
                    />
                  </td>
                </tr>
                {/* Payment Amount */}
                <tr>
                  <td className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {isEditMode && (
                        <button
                          type="button"
                          onClick={() => {
                            setIsCorrectionMode(!isCorrectionMode);
                            setCorrectionReason("");
                          }}
                          className={`text-[10px] px-2 py-1 rounded transition-colors ${
                            isCorrectionMode 
                              ? "bg-gray-200 text-gray-700" 
                              : "bg-red-50 text-red-600 hover:bg-red-100"
                          }`}
                        >
                          {isCorrectionMode ? "Cancel" : "Edit Payment"}
                        </button>
                      )}
                      Payment Amount (৳) <span className="text-red-600">*</span>
                    </div>
                  </td>
                  <td className="border border-gray-200 py-2 px-3">
                    <input
                      type="number"
                      name="paymentAmount"
                      value={formData.paymentAmount}
                      onChange={handleInputChange}
                      readOnly={isEditMode && !isCorrectionMode}
                      className={`input-field text-end ${
                        isEditMode && !isCorrectionMode ? "bg-gray-100 cursor-not-allowed" : "bg-white"
                      } ${isCorrectionMode ? "border-red-400 ring-1 ring-red-400" : ""}`}
                      placeholder="0"
                      step="1" 
                      disabled={isSearching}
                    />
                    {isCorrectionMode && (
                      <div className="mt-2">
                        <textarea
                          placeholder="Why are you changing this amount? (Required)"
                          className="w-full p-2 text-xs border border-red-300 rounded outline-none focus:border-red-500"
                          value={correctionReason}
                          onChange={(e) => setCorrectionReason(e.target.value)}
                          rows="2"
                        />
                        {correctionReason.length < 5 && (
                          <p className="text-[10px] text-red-500 mt-1">Please provide a valid reason.</p>
                        )}
                      </div>
                    )}
                    {errors.paymentAmount && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.paymentAmount}
                      </p>
                    )}
                  </td>
                </tr>
                {/* Admitted-patient 0 balance (credit bill) — permitted roles only */}
                {canMakeZeroBill && (
                  <tr>
                    <td colSpan="2" className="border border-gray-200 py-2 px-3">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={isAdmittedZeroBill}
                          onChange={(e) => setIsAdmittedZeroBill(e.target.checked)}
                          disabled={isSearching || (isEditMode && !isCorrectionMode)}
                          className="h-4 w-4"
                        />
                        <span className="text-sm text-M-heading-color">
                          Admitted Patient — allow 0 Balance (Credit Bill)
                        </span>
                      </label>
                    </td>
                  </tr>
                )}
                {/* Due Or Return Amount */}
                <tr>
                  {(formData.dueAmount > 0 || returnAmount > 0) && (
                    <>
                      {formData.dueAmount > 0 && (
                        <>
                          <td className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                            Due Amount (৳)
                          </td>
                          <td className="border border-gray-200 py-2 px-3">
                            <input
                              type="number"
                              value={formData.dueAmount}
                              readOnly
                              className="input-field bg-gray-100 text-end"
                              step="0.01"
                            />
                          </td>
                        </>
                      )}
                      {returnAmount > 0 && (
                        <>
                          <td className="text-right text-sm text-black border border-gray-300 py-2 px-3 font-medium">
                            Return Amount (৳)
                          </td>
                          <td className="border border-gray-200 py-2 px-3">
                            <input
                              type="number"
                              value={returnAmount}
                              readOnly
                              className="input-field bg-gray-100 text-end"
                              step="0.01"
                            />
                          </td>
                        </>
                      )}
                    </>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

        {/* Modal Footer */}
        <div className="flex justify-end gap-3 mt-5 md:fixed p-5 bottom-0 w-full bg-[#ececec] left-0 z-10">
          <button
            onClick={resetForm}
            className="bg-gray-500/90 text-sm text-white py-2 px-6 rounded-md hover:bg-gray-800 transition-all duration-300"
            disabled={isSearching}
          >
            Reset
          </button>
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            disabled={isSearching}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveAndPrint}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isSearching}
          >
            {processingAction === 'print' 
                ? (formData.id ? "Updating & Printing..." : "Saving & Printing...") 
                : (formData.id ? "Update & Print" : "Save & Print")
              }
          </button>
          <button
            onClick={() => handleSave(false)}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isSearching}
          >
            {processingAction === 'save'
              ? (formData.id ? "Updating..." : "Saving...") 
              : (formData.id ? "Update" : "Save")
            }
          </button>
        </div>

        {showPrintPreview && printData && (
          <div className="mt-6 print-preview p-0">
            <PathologyPrintSlip
              className="print-preview"
              title="Pathology Invoice"
              billInfo={printData.billInfo}
              table={printData.table}
              summary={printData.summary}
              footer={printData.signatureFooter}
              note={printData.note}
              isPaid={printData.isPaid}
              isFree={printData.isFree}
              amountInWords={printData.amountInWords}
            />
          </div>
        )}
      {isNewPatientModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0">
          <div className="bg-white rounded-lg shadow-lg p-6 w-96">
            <h2 className="text-xl font-bold text-gray-700 mb-4">
              Add New Patient
            </h2>
            <div className="mb-4">
              <label className="label">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={newPatient.name}
                onChange={handleNewPatientInputChange}
                className={`input-field ${
                  errors.name ? "border-red-500" : ""
                }`}
                placeholder="Enter patient name"
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>
            <div className="mb-4">
              <label className="label">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="phoneNumber"
                value={newPatient.phoneNumber}
                onChange={handleNewPatientInputChange}
                className={`input-field ${
                  errors.phoneNumber ? "border-red-500" : ""
                }`}
                placeholder="Enter phone number"
              />
              {errors.phoneNumber && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.phoneNumber}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsNewPatientModalOpen(false)}
                className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleAddNewPatient}
                className={`bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200 ${
                  isLoading ? "opacity-70 cursor-not-allowed" : ""
                }`}
                disabled={loading}
              >
                {isLoading ? "Adding..." : "Add Patient"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    {duplicateModal.open && (
      <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-2">
            Duplicate Bill Warning
          </h3>
          <p className="text-sm text-gray-600 mb-4 whitespace-pre-line">
            {duplicateModal.message}
          </p>
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => resolveDuplicate(false)}
              className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            >
              Cancel
            </button>
            <button
              onClick={() => resolveDuplicate(true)}
              className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            >
              Yes, Create New Bill
            </button>
          </div>
        </div>
      </div>
    )}
    <PathologyAgentModal
      isOpen={isAgentModalOpen}
      onClose={() => setIsAgentModalOpen(false)}
      onSave={handleAgentSaveSuccess}
    />
  </div>
);
};

export default AddBill;